import Link from 'components/Basic/Link/Link';
import { SocialMediaItemApi } from 'graphql/generated';
import { getStringWithoutLeadingSlash } from 'helpers/parsing/stringWithoutSlash';
import useTranslation from 'next-translate/useTranslation';
import getConfig from 'next/config';
import twMerge from 'utils/twMerge';

export const SocialMediaIcon: FC<SocialMediaItemApi> = ({
    mainImage,
    mainImageTitle,
    hoverImage,
    hoverImageTitle,
    linkActionTitle,
    socialMediaName,
    url,
}) => {
    const { publicRuntimeConfig } = getConfig();
    const { t } = useTranslation();
    const cdnUrl = publicRuntimeConfig.cdnUrl;

    const fallbackLinkActionTitle = socialMediaName
        ? `${socialMediaName} - ${t('open social media page')}`
        : t('open social media page');

    const getSocialMediaImageUrlWithCdn = (imageUrl?: string) => {
        if (!imageUrl) {
            return '';
        }

        if (/^https?:\/\//i.test(imageUrl) || (cdnUrl && imageUrl.startsWith(cdnUrl))) {
            return imageUrl;
        }

        return `${cdnUrl}/${getStringWithoutLeadingSlash(imageUrl)}`;
    };

    if (!mainImage) {
        return null;
    }

    return (
        <Link href={url} linkType="external" target="_blank" title={linkActionTitle ?? fallbackLinkActionTitle}>
            <div
                aria-label={t('Social media icon')}
                role="img"
                className={twMerge(
                    'relative h-12 w-12 overflow-hidden transition-transform duration-300 hover:scale-110',
                )}
            >
                <img
                    alt={mainImageTitle ?? t('Social media icon - main image')}
                    className="absolute inset-0 h-full w-full object-contain transition-opacity duration-300"
                    src={getSocialMediaImageUrlWithCdn(mainImage)}
                />
                {hoverImage && (
                    <img
                        alt={hoverImageTitle ?? t('Social media icon - hover image')}
                        className="absolute inset-0 h-full w-full object-contain opacity-0 transition-opacity duration-300 hover:opacity-100"
                        src={getSocialMediaImageUrlWithCdn(hoverImage)}
                    />
                )}
            </div>
        </Link>
    );
};
